import { useEffect, useState } from 'react'
import {
  CircleAlert, Download, Eye, File, FileArchive, FileImage, FileSpreadsheet, FileText, LoaderCircle, Presentation, X,
} from 'lucide-react'
import type { Bestand } from '../../../gedeeld/types'
import { c } from '../../lib/api'
import {
  bekijkUrl, grootte, isVerlopen, kanBekijken, leesbaarZin, NIET_BEWAARD, plaatjeVan, SOORT_LABEL, soortVan, teDownloaden,
  type BestandSoort,
} from '../../lib/bestanden'
import { datum, datumTijd } from '../../lib/opmaak'
import { useMag } from '../../lib/staat'
import { Lade, Laden } from '../ui'
import type { Bijlage } from './useBijlagen'

/* ===========================================================================
 *  Bestanden in een gesprek
 *
 *  Wat jij meestuurt, staat klein onder je vraag: naam, soort, grootte, en
 *  "niet bewaard". Niet klikbaar, want er is niets om naar te klikken --
 *  Cowork las het voor die ene vraag en gooide het weg.
 *
 *  Wat Cowork maakt, staat als kaart onder het antwoord, met een knop om te
 *  downloaden zolang hij bewaard wordt (een week), en bij een PDF of foto
 *  ook om hem in een nieuw tabblad te bekijken.
 * =========================================================================== */

function Plaatje({ soort, grootte: g = 16 }: { soort: BestandSoort; grootte?: number }) {
  switch (soort) {
    case 'excel': case 'csv': return <FileSpreadsheet size={g} />
    case 'beeld': return <FileImage size={g} />
    case 'zip': return <FileArchive size={g} />
    case 'powerpoint': return <Presentation size={g} />
    case 'pdf': case 'word': case 'tekst': return <FileText size={g} />
    default: return <File size={g} />
  }
}

export function BestandIcoon({ naam, mime, groot }: { naam: string; mime: string; groot?: boolean }) {
  const soort = soortVan(naam, mime)
  return (
    <span className={`bs-icoon bs-${soort} ${groot ? 'groot' : ''}`} aria-hidden>
      <Plaatje soort={soort} grootte={groot ? 20 : 15} />
    </span>
  )
}

const typeEnGrootte = (b: { naam: string; mime: string; grootte: number }) => `${SOORT_LABEL[soortVan(b.naam, b.mime)]} · ${grootte(b.grootte)}`

/* ------------------------------------------------------------------ *
 *  Boven het invoerveld, terwijl je hem toevoegt
 * ------------------------------------------------------------------ */

export function BijlageChip({ b, onWeg }: { b: Bijlage; onWeg: () => void }) {
  const bezig = !b.bestand && !b.fout
  const sub = b.fout
    ? b.fout
    : bezig
      ? `Uploaden… ${Math.round(b.voortgang * 100)}%`
      : [grootte(b.grootte), leesbaarZin(b.bestand?.leesbaar)].filter(Boolean).join(' · ')
  return (
    <div className={`bijlage-chip ${b.fout ? 'fout' : ''} ${bezig ? 'bezig' : ''}`} title={`${b.naam}\n${sub}`}>
      {b.voorbeeld && !b.fout
        ? <img className="bijlage-plaatje" src={b.voorbeeld} alt="" />
        : b.fout ? <span className="bs-icoon fout" aria-hidden><CircleAlert size={15} /></span>
          : <BestandIcoon naam={b.naam} mime={b.mime} />}
      <span className="bijlage-tekst">
        <span className="bijlage-naam">{b.naam}</span>
        <span className="bijlage-sub" role={b.fout ? 'alert' : undefined}>
          {bezig && <LoaderCircle size={11} className="draai" aria-hidden />} {sub}
        </span>
      </span>
      <button type="button" className="bijlage-weg" onClick={onWeg} aria-label={`${b.naam} weghalen`} title="Weghalen">
        <X size={13} />
      </button>
      {bezig && <span className="bijlage-balk" style={{ width: `${Math.max(4, b.voortgang * 100)}%` }} aria-hidden />}
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Onder je vraag: wat je meestuurde
 * ------------------------------------------------------------------ */

export function MeegestuurdeBestanden({ bestanden }: { bestanden: Bestand[] }) {
  return (
    <ul className="meegestuurd" aria-label="Meegestuurde bestanden">
      {bestanden.map((b) => {
        const plaatje = plaatjeVan(b.id)
        const lees = leesbaarZin(b.leesbaar)
        return (
          <li key={b.id} className="meegestuurd-chip"
            title={[b.naam, lees, b.samenvatting, 'Niet bewaard: alleen gelezen voor deze vraag.'].filter(Boolean).join('\n')}>
            {plaatje ? <img className="bijlage-plaatje" src={plaatje} alt="" /> : <BestandIcoon naam={b.naam} mime={b.mime} />}
            <span className="bijlage-tekst">
              <span className="bijlage-naam">{b.naam}</span>
              <span className="bijlage-sub">{typeEnGrootte(b)} · <span className="niet-bewaard">niet bewaard</span></span>
            </span>
          </li>
        )
      })}
    </ul>
  )
}

/* ------------------------------------------------------------------ *
 *  Onder het antwoord: wat Cowork maakte
 * ------------------------------------------------------------------ */

export function BestandKaart({ b, compact }: { b: Bestand; compact?: boolean }) {
  const soort = soortVan(b.naam, b.mime)
  const verlopen = isVerlopen(b)
  const kan = teDownloaden(b)
  return (
    <div className={`bestand-kaart bs-${soort} ${verlopen ? 'verlopen' : ''} ${compact ? 'compact' : ''}`}>
      <BestandIcoon naam={b.naam} mime={b.mime} groot />
      <div className="bestand-info">
        <strong className="bestand-naam" title={b.naam}>{b.naam}</strong>
        <span className="bestand-meta">{typeEnGrootte(b)}{b.samenvatting ? ` · ${b.samenvatting}` : ''}</span>
        {b.verlooptOp && (
          <span className={`bestand-houdbaar ${verlopen ? 'weg' : ''}`} title={datumTijd(b.verlooptOp)}>
            {verlopen ? `Verlopen op ${datum(b.verlooptOp)}` : `Te downloaden tot ${datum(b.verlooptOp)}`}
          </span>
        )}
      </div>
      <div className="bestand-knoppen">
        {kan && b.url && kanBekijken(soort) && (
          <a className="knop k-stil klein" href={bekijkUrl(b.url)} target="_blank" rel="noopener noreferrer"
            aria-label={`${b.naam} bekijken in een nieuw tabblad`}>
            <Eye size={14} /> <span className="verberg-mini">Bekijken</span>
          </a>
        )}
        {kan && b.url
          ? (
            <a className="knop k-gewoon klein" href={b.url} download={b.naam} aria-label={`${b.naam} downloaden`}>
              <Download size={14} /> <span className="verberg-mini">Downloaden</span>
            </a>
          )
          : <button type="button" className="knop k-gewoon klein" disabled>{verlopen ? 'Verlopen' : 'Niet beschikbaar'}</button>}
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ *
 *  Het paneel: alles van dit gesprek bij elkaar
 * ------------------------------------------------------------------ */

export function BestandenPaneel({ gesprekId, onSluit }: { gesprekId: string; onSluit: () => void }) {
  const [lijst, setLijst] = useState<Bestand[] | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  const magBijlagen = useMag('cowork.bijlagen')
  const magMaken = useMag('cowork.bestanden')

  useEffect(() => {
    let weg = false
    c('bestanden', { gesprekId })
      .then((a) => { if (!weg) setLijst(a.bestanden) })
      .catch((e) => { if (!weg) setFout(e instanceof Error ? e.message : String(e)) })
    return () => { weg = true }
  }, [gesprekId])

  const gemaakt = lijst?.filter((b) => b.soort === 'gemaakt') ?? []
  const geupload = lijst?.filter((b) => b.soort === 'geupload') ?? []

  return (
    <Lade onSluit={onSluit} breed={420} label="Bestanden in dit gesprek">
      <div className="lade-kop">
        <h2 className="paneel-titel">Bestanden in dit gesprek</h2>
        <span className="vul" />
        <button type="button" className="kopbalk-knop" onClick={onSluit} aria-label="Sluiten"><X size={18} /></button>
      </div>
      <div className="lade-lijf paneel-lijf">
        {!lijst && !fout && <Laden />}
        {fout && <p className="foutvlak">{fout}</p>}
        {lijst && !lijst.length && (
          <p className="paneel-leeg">
            Nog geen bestanden.
            {/* Alleen wat voor deze persoon aanstaat (cowork.bijlagen, cowork.bestanden). */}
            {magBijlagen && ' Voeg er een toe met de paperclip.'}
            {magMaken && ' Vraag Cowork om een Excel, CSV, PDF, Word of ZIP te maken.'}
          </p>
        )}
        {gemaakt.length > 0 && (
          <section aria-labelledby="paneel-gemaakt">
            <h3 id="paneel-gemaakt" className="paneel-kop">Gemaakt door Cowork</h3>
            <div className="paneel-kaarten">{gemaakt.map((b) => <BestandKaart key={b.id} b={b} compact />)}</div>
          </section>
        )}
        {geupload.length > 0 && (
          <section aria-labelledby="paneel-geupload">
            <h3 id="paneel-geupload" className="paneel-kop">Meegestuurd</h3>
            <p className="paneel-uitleg">{NIET_BEWAARD} Voor een vervolgvraag voeg je ze opnieuw toe.</p>
            <MeegestuurdeBestanden bestanden={geupload} />
          </section>
        )}
      </div>
    </Lade>
  )
}
